"use client";

import React, { useEffect, useState } from "react";
import "@/src/components/Home/LandingV6/landing-v6.css";
import { Nav } from "@/src/components/Home/LandingV6/ui";
import { Footer } from "@/src/components/Home/LandingV6/blocks";
import {
  WaveRule,
  TapeSticker,
  CornerPin,
  OutlineDisplay,
  Asterism,
  Sparkle,
} from "@/src/components/Home/LandingV6/ornaments";

const LAST_UPDATED = "April 24, 2026";
const EFFECTIVE = "April 24, 2026";

const TOC = [
  { id: "acceptance", label: "acceptance of terms" },
  { id: "eligibility", label: "who can use Quarkle" },
  { id: "account", label: "your account" },
  { id: "subscriptions", label: "subscriptions & payments" },
  { id: "content", label: "your content & license" },
  { id: "ai-output", label: "AI features & output" },
  { id: "acceptable", label: "acceptable use" },
  { id: "ip", label: "our intellectual property" },
  { id: "feedback", label: "feedback" },
  { id: "third-party", label: "third-party services" },
  { id: "termination", label: "suspension & termination" },
  { id: "warranty", label: "no warranties" },
  { id: "liability", label: "limitation of liability" },
  { id: "indemnity", label: "indemnification" },
  { id: "disputes", label: "arbitration & disputes" },
  { id: "law", label: "governing law" },
  { id: "export", label: "export & sanctions" },
  { id: "changes", label: "changes to these terms" },
  { id: "misc", label: "miscellaneous" },
  { id: "contact", label: "contact" },
];

export default function TermsOfServicePage() {
  const [theme, setTheme] = useState("paper");

  useEffect(() => {
    try {
      const s = window.localStorage.getItem("qv6-theme");
      if (s === "paper" || s === "midnight") setTheme(s);
    } catch (_) {}
  }, []);

  return (
    <div className={`qv6 ${theme === "midnight" ? "theme-midnight" : ""}`}>
      <Nav />

      {/* Hero */}
      <section style={{ padding: "70px 40px 40px", position: "relative", overflow: "hidden" }}>
        <div aria-hidden style={{ position: "absolute", top: 40, right: -40, opacity: 0.1, pointerEvents: "none" }}>
          <OutlineDisplay text="the rules." size={260} color="var(--pink)" strokeW={1.8} />
        </div>
        <div aria-hidden style={{ position: "absolute", bottom: 20, left: 60 }}>
          <Asterism size={22} color="var(--clay)" />
        </div>
        <div aria-hidden style={{ position: "absolute", top: 90, right: 120 }}>
          <Sparkle size={30} color="var(--pink)" style={{ animation: "qv6-sparkle 3s infinite" }} />
        </div>
        <div style={{ maxWidth: 960, margin: "0 auto", position: "relative", textAlign: "center" }}>
          <div style={{ display: "inline-flex", gap: 12, alignItems: "center", marginBottom: 14 }}>
            <WaveRule w={60} color="var(--ink)" />
            <span className="caps" style={{ color: "var(--pink)" }}>terms of service · the house rules</span>
            <WaveRule w={60} color="var(--ink)" />
          </div>
          <h1
            className="tanker"
            style={{
              fontSize: "clamp(56px, 8vw, 140px)",
              lineHeight: 0.88,
              letterSpacing: "-0.04em",
              margin: "0 0 18px",
            }}
          >
            the <span className="display" style={{ fontStyle: "italic", color: "var(--pink)" }}>terms</span>, honestly.
          </h1>
          <p className="display" style={{ fontSize: 22, fontStyle: "italic", opacity: 0.85, maxWidth: 720, margin: "0 auto 22px", lineHeight: 1.5 }}>
            the legal language had to be here. we put the plain-english version at the top, and kept the rest as readable as a contract can be.
          </p>
          <div style={{ display: "inline-flex", gap: 12, alignItems: "center", flexWrap: "wrap", justifyContent: "center" }}>
            <CornerPin>last updated · {LAST_UPDATED}</CornerPin>
            <TapeSticker color="var(--lime)" rotate={-2}>binding agreement · please read</TapeSticker>
          </div>
        </div>
      </section>

      <AtAGlance />
      <TableOfContents />

      <LegalBody>
        <Intro />

        <Section id="acceptance" n="01" title="acceptance of terms" accent="var(--pink)">
          <P>
            These Terms of Service (the "<strong>Terms</strong>") are a binding legal agreement between you ("<strong>you</strong>," "<strong>your</strong>") and <strong>Quarkle Incorporated</strong>, a Delaware corporation ("<strong>Quarkle</strong>," "<strong>we</strong>," "<strong>us</strong>," or "<strong>our</strong>"). They govern your access to and use of quarkle.ai, the Quarkle applications, and all related features, tools, and services (collectively, the "<strong>Service</strong>").
          </P>
          <P>
            By creating an account, clicking "I agree," or otherwise accessing or using the Service, you agree to these Terms and our <A href="/privacy">Privacy Policy</A>. If you do not agree, do not use the Service.
          </P>
        </Section>

        <Section id="eligibility" n="02" title="who can use Quarkle" accent="var(--clay)">
          <P>
            You must be at least <strong>16 years of age</strong> (or the age of digital consent in your country, whichever is higher) and have the legal capacity to enter into a contract to use the Service. If you use the Service on behalf of an organization, you represent that you have authority to bind that organization to these Terms, and "<strong>you</strong>" refers to both you and that organization.
          </P>
          <P>
            You may not use the Service if you are located in, or a resident of, any country subject to a comprehensive U.S. embargo, or if you are a person or entity on any U.S. government restricted-party list.
          </P>
        </Section>

        <Section id="account" n="03" title="your account" accent="var(--moss-2)">
          <ul className="qv6-list">
            <li>You must provide accurate, current, and complete information when you register and keep it up to date.</li>
            <li>You are responsible for maintaining the security of your credentials and for all activity under your account. Notify us immediately at <Email>team@quarkle.ai</Email> of any unauthorized use.</li>
            <li>One account per person. You may not sell, transfer, or share your account without our prior written consent.</li>
            <li>We may refuse registration, suspend, or terminate accounts that we reasonably believe violate these Terms or applicable law.</li>
          </ul>
        </Section>

        <Section id="subscriptions" n="04" title="subscriptions & payments" accent="var(--sky)">
          <SubH>Plans, trials & fees</SubH>
          <ul className="qv6-list">
            <li>The Service offers free and paid plans. Eligible signups (for example, via referral) may receive a time-limited free trial of Quarkle Pro. Current pricing is listed on the <A href="/pricing">pricing page</A>.</li>
            <li>Paid plans renew automatically for successive billing periods (monthly or annual, as selected) at the then-current rate until you cancel. You authorize us and our payment processor (Stripe) to charge your payment method on each renewal.</li>
            <li>Fees are quoted in U.S. dollars unless otherwise stated and are exclusive of applicable taxes, which you are responsible for paying.</li>
            <li>We may change pricing, plan features, or billing terms. For existing paid subscribers, material changes will take effect no earlier than your next renewal after reasonable notice (typically by email).</li>
          </ul>
          <SubH>Cancellation & refunds</SubH>
          <ul className="qv6-list">
            <li>You may cancel your paid subscription at any time from your account settings. Cancellation takes effect at the end of the current billing period. You keep Pro access until then.</li>
            <li>Except where required by applicable law, <strong>all fees are non-refundable and we do not provide refunds or credits for partial periods, unused features, or downgrades</strong>. If you believe you were charged in error, email <Email>team@quarkle.ai</Email> within 30 days of the charge and we will review in good faith.</li>
            <li>If a payment fails, we may suspend paid features until the payment is resolved.</li>
          </ul>
        </Section>

        <Section id="content" n="05" title="your content & license" accent="var(--pink)">
          <P>
            "<strong>Your Content</strong>" means any text, manuscript, outline, character profile, note, or other material you submit, upload, or generate through the Service.
          </P>
          <Callout accent="var(--pink)">
            <strong>You own Your Content.</strong> Quarkle claims no ownership of your manuscripts. What's yours stays yours.
          </Callout>
          <P>
            To operate the Service, you grant Quarkle a <strong>worldwide, non-exclusive, royalty-free license</strong> to host, store, reproduce, modify (only as needed for formatting and display), create derivative works from (only as needed to deliver features you ask for, such as edits, summaries, and exports), transmit, and display Your Content, solely for the purposes of providing, maintaining, securing, and improving the Service for you. This license ends when you or we delete Your Content, except that:
          </P>
          <ul className="qv6-list">
            <li>we may retain residual copies in backups, logs, or caches for a reasonable period consistent with our <A href="/privacy">Privacy Policy</A>;</li>
            <li>where you have shared Your Content with others through the Service, it may persist in their copies; and</li>
            <li>we may retain Your Content as required by law or to enforce these Terms.</li>
          </ul>
          <P>
            <strong>We do not use Your Content to train our own AI models, and we do not license Your Content to third parties for training their models.</strong> Our AI-provider contracts prohibit the training use of your inputs and outputs.
          </P>
          <P>
            You represent and warrant that (a) you own Your Content or have all rights necessary to grant the license above; (b) Your Content, and your use of it with the Service, does not infringe any third party's intellectual property, privacy, publicity, or other rights; and (c) Your Content complies with these Terms and applicable law.
          </P>
        </Section>

        <Section id="ai-output" n="06" title="AI features & output" accent="var(--moss-2)">
          <P>
            Quarkle uses machine-learning models (from Quarkle and third-party providers) to suggest edits, critiques, brainstorms, summaries, and other writing aids ("<strong>AI Output</strong>"). You acknowledge and agree that:
          </P>
          <ul className="qv6-list">
            <li>AI Output is generated automatically and <strong>may be inaccurate, biased, offensive, out-of-date, or otherwise unsuitable for your purpose</strong>. You are solely responsible for reviewing AI Output before relying on or publishing it.</li>
            <li>AI Output is not legal, medical, financial, psychological, or other professional advice.</li>
            <li>Because of the nature of generative AI, similar or identical output may be generated for other users. To the extent permitted by applicable law, Quarkle assigns to you any rights Quarkle may have in the AI Output produced in response to your input, subject to these Terms. Whether AI Output is protectable by copyright is determined by applicable law, and we make no representation about copyrightability.</li>
            <li>You will not use the Service, or AI Output, to (i) represent AI Output as human-authored where the context legally requires disclosure; (ii) develop, train, or improve a competing model; or (iii) generate content that violates these Terms or applicable law.</li>
          </ul>
        </Section>

        <Section id="acceptable" n="07" title="acceptable use" accent="var(--clay)">
          <P>You agree not to, and not to permit anyone else to:</P>
          <ul className="qv6-list">
            <li>use the Service to generate, store, or distribute content that is unlawful, defamatory, harassing, fraudulent, obscene, sexually exploitative of minors, or that infringes any third-party rights;</li>
            <li>impersonate another person or misrepresent your affiliation with anyone;</li>
            <li>interfere with, disrupt, or attempt to gain unauthorized access to the Service, other accounts, or related systems;</li>
            <li>use bots, scrapers, or other automated means to access the Service, other than as explicitly permitted by an official API;</li>
            <li>reverse-engineer, decompile, disassemble, or attempt to derive the source code of the Service, except to the extent such restriction is prohibited by applicable law;</li>
            <li>use the Service to build, train, or evaluate a competing AI product or model, or to benchmark against Quarkle without our prior written consent;</li>
            <li>remove or obscure any proprietary notices in or on the Service;</li>
            <li>resell, sublicense, or commercially exploit the Service except as expressly permitted by your plan;</li>
            <li>use the Service in violation of any applicable law, regulation, or third-party agreement, including export-control and sanctions laws.</li>
          </ul>
          <P>
            We may, but are not obligated to, review content and activity on the Service and may remove content or suspend accounts that we reasonably believe violate these Terms, harm the Service, or expose us or our users to liability.
          </P>
        </Section>

        <Section id="ip" n="08" title="our intellectual property" accent="var(--sky)">
          <P>
            The Service, including its software, design, user interface, text, graphics, logos, names ("Quarkle," the Quarkle marks), and all related intellectual property, are owned by Quarkle or our licensors and are protected by copyright, trademark, and other laws.
          </P>
          <P>
            Subject to your compliance with these Terms, we grant you a <strong>limited, personal, non-exclusive, non-transferable, non-sublicensable, revocable license</strong> to access and use the Service for your own internal or personal writing purposes. No other rights are granted, by implication or otherwise. All rights not expressly granted are reserved by Quarkle.
          </P>
        </Section>

        <Section id="feedback" n="09" title="feedback" accent="var(--lime)">
          <P>
            If you send us ideas, suggestions, bug reports, or other feedback about the Service ("<strong>Feedback</strong>"), you grant Quarkle a <strong>perpetual, irrevocable, worldwide, royalty-free, fully sublicensable license</strong> to use, modify, and exploit that Feedback for any purpose, without restriction or compensation to you. You acknowledge that Feedback is not confidential unless we agree otherwise in writing.
          </P>
        </Section>

        <Section id="third-party" n="10" title="third-party services" accent="var(--moss-2)">
          <P>
            The Service integrates with and depends on third-party services (including cloud infrastructure, authentication, payment processing, analytics, and AI providers). Your use of those third-party services is governed by the third party's own terms and privacy practices. Quarkle is not responsible for third-party services, and linking to them is not an endorsement.
          </P>
        </Section>

        <Section id="termination" n="11" title="suspension & termination" accent="var(--clay)">
          <ul className="qv6-list">
            <li><strong>By you:</strong> You may stop using the Service or cancel your subscription at any time from your account settings.</li>
            <li><strong>By us:</strong> We may suspend or terminate your account or access to the Service, in whole or in part, immediately and without refund, if we reasonably believe you have violated these Terms or applicable law, or if suspension is necessary to protect the Service, other users, or third parties. We will use reasonable efforts to provide notice where we can.</li>
            <li><strong>Effect:</strong> On termination, your right to access the Service ends. Sections that by their nature should survive (including ownership, licenses granted to us, disclaimers, limitation of liability, indemnification, arbitration, and governing law) will survive termination. We may delete Your Content after a reasonable grace period in accordance with our <A href="/privacy">Privacy Policy</A>.</li>
          </ul>
        </Section>

        <Section id="warranty" n="12" title="no warranties" accent="var(--pink)">
          <P style={{ textTransform: "none" }}>
            <strong>THE SERVICE AND ALL AI OUTPUT ARE PROVIDED "AS IS" AND "AS AVAILABLE," WITHOUT WARRANTY OF ANY KIND.</strong> TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, QUARKLE AND ITS AFFILIATES, OFFICERS, EMPLOYEES, AGENTS, SUPPLIERS, AND LICENSORS <strong>DISCLAIM ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE</strong>, INCLUDING ANY IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, ACCURACY, QUIET ENJOYMENT, AND NON-INFRINGEMENT.
          </P>
          <P>
            We do not warrant that the Service will be uninterrupted, error-free, secure, or free of harmful components; that defects will be corrected; or that the Service or AI Output will meet your requirements or expectations. Some jurisdictions do not allow the exclusion of certain warranties; in those cases, the exclusions above apply to the maximum extent permitted.
          </P>
        </Section>

        <Section id="liability" n="13" title="limitation of liability" accent="var(--clay)">
          <P style={{ textTransform: "none" }}>
            <strong>TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT WILL QUARKLE (OR ITS AFFILIATES, OFFICERS, EMPLOYEES, AGENTS, SUPPLIERS, OR LICENSORS) BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR FOR ANY LOSS OF PROFITS, REVENUES, GOODWILL, DATA, OR MANUSCRIPTS, ARISING OUT OF OR RELATED TO THESE TERMS OR THE SERVICE, WHETHER BASED ON WARRANTY, CONTRACT, TORT (INCLUDING NEGLIGENCE), STATUTE, OR ANY OTHER THEORY, AND WHETHER OR NOT QUARKLE HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.</strong>
          </P>
          <P style={{ textTransform: "none" }}>
            <strong>OUR TOTAL CUMULATIVE LIABILITY FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THESE TERMS OR THE SERVICE WILL NOT EXCEED THE GREATER OF (A) THE AMOUNTS YOU PAID US FOR THE SERVICE IN THE TWELVE (12) MONTHS IMMEDIATELY PRECEDING THE EVENT GIVING RISE TO THE LIABILITY, OR (B) USD $100.</strong>
          </P>
          <P>
            These limitations form an essential basis of the bargain between you and Quarkle and will apply even if a limited remedy fails of its essential purpose. Some jurisdictions do not allow certain limitations; in those cases, the limitations above apply to the maximum extent permitted.
          </P>
        </Section>

        <Section id="indemnity" n="14" title="indemnification" accent="var(--moss-2)">
          <P>
            You will defend, indemnify, and hold harmless Quarkle and our affiliates, officers, directors, employees, agents, and licensors from and against any third-party claims, liabilities, damages, losses, and expenses (including reasonable attorneys' fees) arising out of or relating to (a) Your Content; (b) your use or misuse of the Service or AI Output; (c) your violation of these Terms; or (d) your violation of any law or third-party right. We may participate in the defense with counsel of our choice at our expense, and you will not settle any claim in a way that imposes obligations on us without our prior written consent.
          </P>
        </Section>

        <Section id="disputes" n="15" title="arbitration & disputes" accent="var(--pink)">
          <Callout accent="var(--pink)">
            <strong>Please read carefully.</strong> This section requires you and Quarkle to resolve most disputes through individual binding arbitration, and waives the right to participate in a class action. You may opt out within 30 days of first accepting these Terms.
          </Callout>
          <SubH>Informal resolution first</SubH>
          <P>
            Before filing any formal dispute, you agree to first contact us at <Email>team@quarkle.ai</Email> and give us 30 days to resolve the issue in good faith.
          </P>
          <SubH>Binding individual arbitration</SubH>
          <P>
            Any dispute, claim, or controversy arising out of or relating to these Terms or the Service that is not resolved informally will be resolved exclusively by <strong>final and binding individual arbitration</strong> administered by JAMS under its Streamlined Arbitration Rules then in effect (or, if JAMS is unavailable, a comparable arbitration provider). The arbitration will be conducted in English, seated in Wilmington, Delaware, and may be held by telephone or video for claims under USD $10,000. Judgment on the award may be entered in any court of competent jurisdiction.
          </P>
          <SubH>Class action waiver</SubH>
          <P>
            <strong>You and Quarkle agree to bring claims only in an individual capacity, not as a plaintiff or class member in any purported class, collective, consolidated, or representative action.</strong> The arbitrator may not consolidate claims or preside over any form of class proceeding. If this waiver is found unenforceable, then the entirety of this arbitration section will be null and void as to the affected claim.
          </P>
          <SubH>Exceptions</SubH>
          <P>
            Either party may (a) bring an individual action in small-claims court for qualifying disputes; and (b) seek injunctive or equitable relief in court to protect intellectual property, confidentiality, or security.
          </P>
          <SubH>Opt-out</SubH>
          <P>
            You may opt out of this arbitration agreement by emailing <Email>team@quarkle.ai</Email> with the subject line "<em>Arbitration Opt-Out</em>" and your account email within 30 days of first accepting these Terms. Opting out will not affect any other part of these Terms.
          </P>
          <SubH>30-day deadline to file</SubH>
          <P>
            Any claim must be filed within one (1) year after it arises, except where applicable law requires a longer period; otherwise, it is permanently barred.
          </P>
        </Section>

        <Section id="law" n="16" title="governing law" accent="var(--sky)">
          <P>
            These Terms are governed by and construed in accordance with the laws of the <strong>State of Delaware</strong>, U.S.A., without regard to its conflict-of-laws rules. The United Nations Convention on Contracts for the International Sale of Goods does not apply. Subject to the arbitration section above, the state and federal courts located in New Castle County, Delaware have exclusive jurisdiction and venue over any dispute not subject to arbitration, and each party consents to such jurisdiction and waives any objection based on inconvenient forum.
          </P>
        </Section>

        <Section id="export" n="17" title="export & sanctions" accent="var(--clay)">
          <P>
            You represent that you are not located in, and will not use the Service from, any country subject to a comprehensive U.S. government embargo, and that you are not on any U.S. government restricted-party list. You agree to comply with all applicable export-control and sanctions laws when using the Service.
          </P>
        </Section>

        <Section id="changes" n="18" title="changes to these terms" accent="var(--moss-2)">
          <P>
            We may update these Terms from time to time. If we make material changes, we will notify you by email or by prominent in-product notice before the changes take effect. The "last updated" date at the top tells you when these Terms most recently changed. If you continue to use the Service after the effective date, you accept the updated Terms. If you do not agree, you must stop using the Service and may cancel your subscription.
          </P>
        </Section>

        <Section id="misc" n="19" title="miscellaneous" accent="var(--lime)">
          <ul className="qv6-list">
            <li><strong>Entire agreement.</strong> These Terms and our <A href="/privacy">Privacy Policy</A> are the entire agreement between you and Quarkle regarding the Service and supersede any prior agreements.</li>
            <li><strong>Severability.</strong> If any provision is found unenforceable, the rest remains in full force and effect, and the unenforceable provision will be modified only to the minimum extent necessary.</li>
            <li><strong>No waiver.</strong> Our failure to enforce any provision is not a waiver of our right to do so later.</li>
            <li><strong>Assignment.</strong> You may not assign these Terms without our prior written consent. We may assign these Terms, in whole or in part, to an affiliate or in connection with a merger, acquisition, or sale of assets.</li>
            <li><strong>Relationship.</strong> Nothing in these Terms creates a partnership, joint venture, employment, or agency relationship.</li>
            <li><strong>Notices.</strong> We may provide notices by posting in the Service or emailing the address associated with your account. You may send legal notices to <Email>team@quarkle.ai</Email> with a copy to the address above.</li>
            <li><strong>Electronic communications.</strong> You consent to receive communications from us electronically, and agree that electronic agreements and signatures satisfy any legal requirement for a writing or signature.</li>
            <li><strong>Force majeure.</strong> Neither party is liable for failures or delays caused by events beyond their reasonable control.</li>
            <li><strong>Headings.</strong> Section titles are for convenience and do not affect interpretation.</li>
          </ul>
        </Section>

        <Section id="contact" n="20" title="contact" accent="var(--pink)">
          <P>Questions about these Terms?</P>
          <ContactCard />
        </Section>

        <Acknowledgment />
      </LegalBody>

      <Footer />
    </div>
  );
}

/* ---------------- Subcomponents ---------------- */

function Intro() {
  return (
    <div
      className="qv6-legal-section"
      style={{
        padding: "0 0 24px",
      }}
    >
      <p style={{ fontSize: 17, lineHeight: 1.7, opacity: 0.92 }}>
        Welcome to Quarkle. These Terms are longer than we'd like them to be, but we wrote them as plainly as we could. The short summary above covers the spirit; this is the letter.
      </p>
    </div>
  );
}

function AtAGlance() {
  const items = [
    { title: "you own your book", body: "you keep full ownership of your manuscripts. we just help you write them.", accent: "var(--pink)" },
    { title: "cancel anytime", body: "stop your subscription in one click. you keep every draft you wrote.", accent: "var(--lime)" },
    { title: "AI can be wrong", body: "AI output may be inaccurate. you're always the final editor of your book.", accent: "var(--clay)" },
    { title: "no training on you", body: "your writing is never used to train our models, or anyone else's.", accent: "var(--sky)" },
  ];
  return (
    <section style={{ padding: "30px 40px 60px", position: "relative" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <span className="caps" style={{ color: "var(--pink)" }}>the plain-english version</span>
          <h2 className="tanker" style={{ fontSize: "clamp(34px, 4.4vw, 56px)", margin: "8px 0 0", lineHeight: 1 }}>
            if you read nothing else, <span className="display" style={{ fontStyle: "italic", color: "var(--pink)" }}>this</span>.
          </h2>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 20 }}>
          {items.map((it, i) => (
            <div
              key={i}
              style={{
                background: "var(--paper-3)",
                border: "1.5px solid var(--ink)",
                borderRadius: 12,
                padding: "22px 22px 20px",
                boxShadow: "6px 6px 0 var(--ink)",
                position: "relative",
              }}
            >
              <div aria-hidden style={{ position: "absolute", top: 12, right: 14, width: 10, height: 10, borderRadius: 999, background: it.accent, boxShadow: "0 0 0 3px var(--paper-3), 0 0 0 4px var(--ink)" }} />
              <h3 className="tanker" style={{ fontSize: 22, margin: "0 0 8px", lineHeight: 1.05 }}>{it.title}</h3>
              <p className="display" style={{ margin: 0, fontSize: 15, lineHeight: 1.5, fontStyle: "italic", opacity: 0.85 }}>{it.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TableOfContents() {
  return (
    <section style={{ padding: "0 40px 60px", position: "relative" }}>
      <div
        style={{
          maxWidth: 1100,
          margin: "0 auto",
          background: "var(--paper-2)",
          border: "1.5px solid var(--ink)",
          borderRadius: 12,
          padding: "22px 24px",
          boxShadow: "6px 6px 0 var(--ink)",
        }}
      >
        <div className="caps" style={{ opacity: 0.7, marginBottom: 12 }}>the full text · contents</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "6px 22px" }}>
          {TOC.map((t, i) => (
            <a
              key={t.id}
              href={`#${t.id}`}
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 10,
                padding: "4px 0",
                borderBottom: "1px dashed var(--rule)",
                color: "var(--ink)",
                textDecoration: "none",
                fontSize: 14,
              }}
            >
              <span className="mono" style={{ fontSize: 10, opacity: 0.55, letterSpacing: "0.12em" }}>
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="display" style={{ fontStyle: "italic", fontSize: 16 }}>{t.label}</span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function LegalBody({ children }) {
  return (
    <section style={{ padding: "0 40px 80px", position: "relative" }}>
      <div style={{ maxWidth: 880, margin: "0 auto" }}>
        {children}
      </div>
      <style jsx global>{`
        .qv6 .qv6-legal-section p { font-size: 17px; line-height: 1.7; margin: 12px 0; color: var(--ink); opacity: 0.92; }
        .qv6 .qv6-legal-section p.muted { opacity: 0.7; font-size: 15px; }
        .qv6 .qv6-legal-section strong { font-weight: 600; }
        .qv6 .qv6-legal-section em { font-style: italic; }
        .qv6 .qv6-legal-section a { color: var(--ink); border-bottom: 1.5px solid var(--pink); text-decoration: none; }
        .qv6 .qv6-legal-section a:hover { background: var(--pink); color: #fff; }
        .qv6 .qv6-list {
          list-style: none;
          padding: 0;
          margin: 14px 0;
          display: grid;
          gap: 10px;
        }
        .qv6 .qv6-list > li {
          padding-left: 22px;
          position: relative;
          font-size: 17px;
          line-height: 1.65;
          opacity: 0.92;
        }
        .qv6 .qv6-list > li::before {
          content: "›";
          position: absolute;
          left: 0;
          top: 0;
          color: var(--pink);
          font-family: 'Gambarino', serif;
          font-weight: 700;
          font-size: 22px;
          line-height: 1.2;
        }
      `}</style>
    </section>
  );
}

function Section({ id, n, title, accent, children }) {
  return (
    <article
      id={id}
      className="qv6-legal-section"
      style={{
        padding: "38px 0 32px",
        borderTop: "1.5px solid var(--ink)",
        scrollMarginTop: 120,
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: 18, marginBottom: 14, flexWrap: "wrap" }}>
        <span className="tanker" style={{ fontSize: 72, color: accent, lineHeight: 0.85, fontStyle: "italic" }}>
          {n}
        </span>
        <div>
          <h2 className="tanker" style={{ fontSize: "clamp(30px, 3.6vw, 48px)", margin: 0, lineHeight: 1, letterSpacing: "-0.02em" }}>
            {title}
          </h2>
          <WaveRule w={100} color={accent} />
        </div>
      </div>
      {children}
    </article>
  );
}

function SubH({ children }) {
  return (
    <h3 className="display" style={{ fontSize: 22, fontStyle: "italic", margin: "22px 0 6px", color: "var(--ink)" }}>
      {children}
    </h3>
  );
}

function P({ children, style }) {
  return <p style={style}>{children}</p>;
}

function A({ href, children }) {
  const external = href?.startsWith("http");
  return (
    <a href={href} target={external ? "_blank" : undefined} rel={external ? "noreferrer" : undefined}>
      {children}
    </a>
  );
}

function Email({ children }) {
  return (
    <a href={`mailto:${children}`} className="mono" style={{ fontSize: 15 }}>
      {children}
    </a>
  );
}

function Callout({ children, accent = "var(--lime)" }) {
  return (
    <aside
      style={{
        background: "var(--paper-3)",
        border: "1.5px solid var(--ink)",
        borderLeft: `6px solid ${accent}`,
        borderRadius: 10,
        padding: "16px 18px",
        margin: "18px 0",
        fontSize: 16,
        lineHeight: 1.55,
        fontFamily: "'Gambarino', serif",
        fontStyle: "italic",
      }}
    >
      {children}
    </aside>
  );
}

function ContactCard() {
  return (
    <div
      style={{
        background: "var(--ink)",
        color: "var(--paper)",
        border: "1.5px solid var(--ink)",
        borderRadius: 12,
        padding: "24px 26px",
        boxShadow: "8px 8px 0 var(--pink)",
        marginTop: 16,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 18,
        flexWrap: "wrap",
      }}
    >
      <div>
        <div className="caps" style={{ color: "var(--pink)", marginBottom: 10 }}>the entity you're contracting with</div>
        <div className="tanker" style={{ fontSize: 28, lineHeight: 1.1 }}>Quarkle Incorporated</div>
        <div className="display" style={{ fontSize: 16, fontStyle: "italic", opacity: 0.85, marginTop: 4 }}>Delaware corporation · operating from Massachusetts</div>
        <div className="mono" style={{ fontSize: 12, opacity: 0.75, marginTop: 10, letterSpacing: "0.12em", textTransform: "uppercase" }}>
          effective {EFFECTIVE}
        </div>
      </div>
      <a
        href="mailto:team@quarkle.ai"
        className="mono"
        style={{
          background: "var(--pink)",
          color: "#fff",
          padding: "12px 18px",
          borderRadius: 999,
          border: "1.5px solid var(--paper)",
          fontSize: 13,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          textDecoration: "none",
          boxShadow: "4px 4px 0 var(--paper-3)",
        }}
      >
        team@quarkle.ai →
      </a>
    </div>
  );
}

function Acknowledgment() {
  return (
    <div
      style={{
        marginTop: 40,
        padding: "26px 28px",
        background: "var(--paper-3)",
        border: "1.5px dashed var(--ink)",
        borderRadius: 12,
        textAlign: "center",
      }}
    >
      <div className="caps" style={{ color: "var(--pink)", marginBottom: 10 }}>acknowledgment</div>
      <p className="display" style={{ fontSize: 18, fontStyle: "italic", lineHeight: 1.6, opacity: 0.9, margin: 0 }}>
        by using Quarkle, you confirm that you have read, understood, and agree to be bound by these Terms. thanks for writing with us.
      </p>
    </div>
  );
}
